import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BosDurum, Buton, Iskelet, Rozet, SatirDugmesi, Sekmeler } from "@koclukpanel/ui";
import { ApiHatasi, onayBekleyenler, type OnayBekleyen } from "./api";
import { RaporDuzenle } from "./Raporlar";
import { GenelOnaylar } from "./GenelOnaylar";

/**
 * ONAY KUYRUGU — kontrol bekleyen veli raporlari
 *
 * ================================================================
 * NEDEN AYRI BIR EKRAN
 * ================================================================
 * Is listesi (F-ANL-09) UZMAN kapsamli: "benim ogrencilerimde ne var"
 * sorusunu cevapliyor. Onaycinin sorusu baska — "kim benden bir sey
 * bekliyor".
 *
 * 31.07.2026'da fark edildi: uzman raporu kontrole gonderiyor, rapor
 * orada kaliyordu. Onaycinin haberi olmuyordu ve rapor akisi uctan uca
 * calismiyordu. Panel uzmana "onay yetkisi olan bir yonetici
 * inceleyecek" diyordu ama o yonetici raporu hicbir yerden goremiyordu.
 *
 * ================================================================
 * IKI KUYRUK, TEK EKRAN — 07.08.2026
 * ================================================================
 * Veli raporu onayi ile GENEL onay motoru (F-APR) ayri uclardan
 * gidiyor ve ayri izinlere bagli. Ama onaycinin sordugu soru tek:
 * "kim benden ne bekliyor". Ikisini ayri menu girdisi yapsaydim,
 * birini bosaltan kisi otekini kontrol etmeyi unuturdu.
 *
 * SEKME, IZNI OLANA GORUNUYOR. Iki izin ayri roller tasiyabilir;
 * tasimadigi sekmeyi gostermek, tiklayinca 403 alacagi bir cikmaz
 * sokak acardi. Tek sekme kaliyorsa sekme cubugu HIC cizilmiyor —
 * tek secenekli bir sekme cubugu kullaniciya secim varmis gibi
 * gosterip hicbir sey sunmaz.
 */
export function OnayKuyrugu({
  raporOnaylayabilir,
  genelOnaylayabilir,
}: {
  raporOnaylayabilir: boolean;
  genelOnaylayabilir: boolean;
}) {
  const [gorunum, setGorunum] = useState<"rapor" | "genel">(raporOnaylayabilir ? "rapor" : "genel");

  const sekmeler = [
    ...(raporOnaylayabilir ? ([["rapor", "Veli raporları"]] as const) : []),
    ...(genelOnaylayabilir ? ([["genel", "Program ve doküman"]] as const) : []),
  ];

  if (sekmeler.length === 0) {
    return (
      <BosDurum
        baslik="Onay yetkiniz yok"
        aciklama="Bu ekran onay bekleyen işleri gösterir. Yetkiniz olmaması, onay bekleyen bir şey olmadığı anlamına gelmez."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        ================================================================
        KUNYE: EKRANIN TEK KURALI BASTA YAZIYOR
        ================================================================
        Cizim (`OnayBekleyenler.dc.html`) basligin altina tek cumle
        koyuyor: "Onaylayan kişi yazan kişi olamaz". Bu ekranin varlik
        sebebi o kural ve kullanici onu, kendi raporunda dugmeyi
        bulamayinca degil ONCEDEN ogrenmeli.

        Cumle bir SUS degil: `kendiRaporum` sunucudan geliyor ve satirda
        gercekten uygulanıyor (F-REP-03).
      */}
      <p className="text-notr-600 text-sm">
        Onaylayan kişi yazan kişi olamaz — iki çift göz kuralı.
      </p>

      {/*
        BESINCI elle yazilmis `role="tab"` seridi kaldirildi (`Roller` ·
        `Loglar` · `Personel` · `Finans` · burasi). Hepsi ayni yarim
        desendi: `aria-selected` var, ok tuslariyla gezinme / gezinen
        odak / `tabpanel` bagi YOK.
      */}
      {sekmeler.length > 1 && (
        <Sekmeler
          etiket="Onay kuyruğu bölümleri"
          secili={gorunum}
          onSec={setGorunum}
          sekmeler={sekmeler.map(([deger, etiket]) => ({ deger, etiket }))}
        />
      )}

      {gorunum === "rapor" && raporOnaylayabilir && <RaporOnaylari />}
      {gorunum === "genel" && genelOnaylayabilir && <GenelOnaylar />}
    </div>
  );
}

function RaporOnaylari() {
  const [acik, setAcik] = useState<{ raporId: string; ogrenciId: string } | null>(null);

  const { data, isPending, isError, error, refetch, isFetching } = useQuery<OnayBekleyen[]>({
    queryKey: ["onay-bekleyenler"],
    queryFn: onayBekleyenler,
  });

  if (acik) {
    return (
      <RaporDuzenle
        raporId={acik.raporId}
        ogrenciId={acik.ogrenciId}
        geriEtiketi="← Onay kuyruğu"
        onGeri={() => {
          setAcik(null);
          // Karar verildiyse rapor kuyruktan dusmus olabilir.
          void refetch();
        }}
      />
    );
  }

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Onay kuyruğu alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.length === 0) {
    return (
      <BosDurum
        ikon="tamam"
        baslik="Onay bekleyen rapor yok"
        aciklama="Kontrole gönderilen bütün raporlar karara bağlanmış."
        eylem={
          <Buton gorunum="sessiz" onClick={() => void refetch()} yukleniyor={isFetching}>
            Yenile
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-notr-600 text-sm">
          {data.length} rapor onay bekliyor
          {/*
            IZIN KODU EKRANDA — cizimden. Rolleri yapilandiran yonetici
            "bu bolumu kim gorur" sorusunu, rol ekranina gidip tahmin
            ederek degil BURADA cevapliyor. Kod uydurulmuyor: bu bolum
            gercekten `rapor.veli.onayla` iznine bagli
            (`Uygulama.tsx` `raporOnaylayabilir`).
          */}
          <span className="text-notr-600"> · izin: </span>
          <code className="text-notr-600 text-xs">rapor.veli.onayla</code>
        </p>
        <Buton gorunum="sessiz" onClick={() => void refetch()} yukleniyor={isFetching}>
          Yenile
        </Buton>
      </div>

      <ul className="flex flex-col gap-2">
        {data.map((r) => (
          <li key={r.id}>
            <SatirDugmesi onClick={() => setAcik({ raporId: r.id, ogrenciId: r.studentId })}>
              <span className="min-w-0 flex-1">
                <span className="text-notr-900 block truncate text-sm font-medium">
                  {r.ogrenciAdi}
                </span>
                <span className="text-notr-600 block truncate text-sm">
                  {donem(r.periodStart, r.periodEnd)} · {r.hazirlayan}
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-2">
                  <span className="text-notr-600 text-xs">{bekleme(r.bekliyorAt)}</span>

                  {/*
                    F-REP-03 — kendi raporunu onaylayamazsin. Kuyruktan
                    GIZLEMIYORUZ: koordinator kendi hazirladigi raporun
                    beklediğini gormeli, yoksa "rapor kayboldu" der.
                    Ama neden onaylayamayacagini ONCEDEN soyluyoruz;
                    tiklayip hata almasin.
                  */}
                  {r.kendiRaporum && <Rozet>Sizin raporunuz — başkası onaylamalı</Rozet>}

                  {/* Ölçülemeyen kapsam "%0" DEĞİL. */}
                  {r.dataCoverage !== null && r.dataCoverage < 50 && (
                    <Rozet ton="uyari" className="ring-uyari-500/20">
                      Düşük veri kapsamı %{Math.round(r.dataCoverage)}
                    </Rozet>
                  )}
                  {r.dataCoverage === null && (
                    <Rozet ton="uyari" className="ring-uyari-500/20">
                      Veri kapsamı ölçülemedi
                    </Rozet>
                  )}
                </span>
              </span>

              <span aria-hidden="true" className="text-marka-700 shrink-0 text-sm">
                İncele →
              </span>
            </SatirDugmesi>
          </li>
        ))}
      </ul>
    </div>
  );
}

function donem(bas: string, bit: string): string {
  const b = new Date(bas);
  const s = new Date(bit);
  return `${b.toLocaleDateString("tr-TR", { day: "numeric", month: "long" })} – ${s.toLocaleDateString(
    "tr-TR",
    { day: "numeric", month: "long", year: "numeric" },
  )}`;
}

/**
 * Ne kadardir bekliyor.
 *
 * Mutlak tarih ("29 Temmuz 14:03") yerine sure yaziliyor: onaycinin
 * sordugu soru "ne zaman gonderildi" degil, "ne kadardir bekletiyorum".
 */
function bekleme(damga: string): string {
  const dakika = Math.floor((Date.now() - new Date(damga).getTime()) / 60000);
  if (dakika < 60) return "az önce gönderildi";
  const saat = Math.floor(dakika / 60);
  if (saat < 24) return `${saat} saattir bekliyor`;
  const gun = Math.floor(saat / 24);
  return `${gun} gündür bekliyor`;
}
